import { useState, useEffect, useRef } from 'react';
import { time, tasks } from '@/lib/api';
import { formatDateTime, formatDuration } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Play, Square, Clock, Timer } from 'lucide-react';
import { toast } from 'sonner';

function ActiveTimer({ entry, onStop }) {
  const [elapsed, setElapsed] = useState(0);
  const startTime = useRef(new Date(entry.start_time));

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const diff = Math.floor((now - startTime.current) / 1000);
      setElapsed(diff);
    }, 1000);
    return () => clearInterval(interval);
  }, [entry]);

  const hours = Math.floor(elapsed / 3600);
  const minutes = Math.floor((elapsed % 3600) / 60);
  const seconds = elapsed % 60;

  return (
    <Card className="border-primary border-2 bg-primary/5">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
              <div className="relative w-4 h-4 bg-primary rounded-full" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Trabajando en</p>
              <h3 className="font-semibold text-lg">{entry.task_title || 'Tarea'}</h3>
            </div>
          </div>
          
          <div className="flex items-center gap-6">
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Tiempo transcurrido</p>
              <p className="font-mono text-3xl font-bold text-primary">
                {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}
              </p>
            </div>
            
            <Button
              size="lg"
              variant="destructive"
              onClick={() => onStop(entry.entry_id)}
              className="gap-2"
              data-testid="stop-timer-btn"
            >
              <Square className="h-5 w-5" />
              Detener
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StartTimerCard({ tasksList, onStart }) {
  const [selectedTask, setSelectedTask] = useState('');

  const handleStart = () => {
    if (!selectedTask) {
      toast.error('Selecciona una tarea');
      return;
    }
    onStart(selectedTask);
    setSelectedTask('');
  };

  const availableTasks = tasksList.filter(t => t.status !== 'completed');

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <Timer className="h-8 w-8 text-muted-foreground" />
          <div className="flex-1">
            <Select value={selectedTask} onValueChange={setSelectedTask}>
              <SelectTrigger data-testid="select-task-timer">
                <SelectValue placeholder="Selecciona una tarea para iniciar" />
              </SelectTrigger>
              <SelectContent>
                {availableTasks.map((task) => (
                  <SelectItem key={task.task_id} value={task.task_id}>
                    {task.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleStart} className="gap-2" data-testid="start-new-timer-btn">
            <Play className="h-4 w-4" />
            Iniciar
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function TimeTracker() {
  const [currentTimer, setCurrentTimer] = useState(null);
  const [entries, setEntries] = useState([]);
  const [tasksList, setTasksList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // Poll for current timer
    const interval = setInterval(loadCurrentTimer, 5000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      const [currentRes, entriesRes, tasksRes] = await Promise.all([
        time.getCurrent(),
        time.getEntries(),
        tasks.getAll(),
      ]);
      setCurrentTimer(currentRes.data);
      setEntries(entriesRes.data);
      setTasksList(tasksRes.data);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentTimer = async () => {
    try {
      const res = await time.getCurrent();
      setCurrentTimer(res.data);
    } catch (err) {
      console.error('Error loading current timer:', err);
    }
  };

  const handleStart = async (taskId) => {
    try {
      const task = tasksList.find(t => t.task_id === taskId);
      await time.start({ task_id: taskId, description: task?.title || '' });
      toast.success('Temporizador iniciado');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al iniciar temporizador');
    }
  };

  const handleStop = async (entryId) => {
    try {
      await time.stop({ entry_id: entryId });
      toast.success('Temporizador detenido');
      loadData();
    } catch (err) {
      toast.error('Error al detener temporizador');
    }
  };

  // Group entries by date
  const entriesByDate = entries.reduce((acc, entry) => {
    if (entry.is_running) return acc;
    const date = new Date(entry.start_time).toLocaleDateString('es-MX');
    if (!acc[date]) acc[date] = [];
    acc[date].push(entry);
    return acc;
  }, {});

  // Calculate totals
  const todayStr = new Date().toLocaleDateString('es-MX');
  const todayEntries = entriesByDate[todayStr] || [];
  const todayTotal = todayEntries.reduce((sum, e) => sum + e.duration_minutes, 0);
  
  const weekTotal = entries
    .filter(e => !e.is_running)
    .reduce((sum, e) => sum + e.duration_minutes, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="time-tracker-page">
      <div>
        <h1 className="text-3xl font-bold font-heading tracking-tight">Time Tracker</h1>
        <p className="text-muted-foreground">Control de tiempo por tareas</p>
      </div>

      {/* Active Timer or Start */}
      {currentTimer ? (
        <ActiveTimer entry={currentTimer} onStop={handleStop} />
      ) : (
        <StartTimerCard tasksList={tasksList} onStart={handleStart} />
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">
              Hoy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading">{formatDuration(todayTotal)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">
              Esta Semana
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading">{formatDuration(weekTotal)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide font-heading">
              Entradas Hoy
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold font-heading">{todayEntries.length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Time Entries */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg uppercase tracking-wide">
            Historial de Tiempo
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(entriesByDate).length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No hay entradas de tiempo registradas
            </p>
          ) : (
            <div className="space-y-6">
              {Object.entries(entriesByDate).map(([date, dateEntries]) => (
                <div key={date}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold">{date}</h3>
                    <Badge variant="outline">
                      {formatDuration(dateEntries.reduce((sum, e) => sum + e.duration_minutes, 0))}
                    </Badge>
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="uppercase text-xs">Tarea</TableHead>
                        <TableHead className="uppercase text-xs">Usuario</TableHead>
                        <TableHead className="uppercase text-xs">Inicio</TableHead>
                        <TableHead className="uppercase text-xs">Fin</TableHead>
                        <TableHead className="uppercase text-xs text-right">Duración</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dateEntries.map((entry) => (
                        <TableRow key={entry.entry_id}>
                          <TableCell className="font-medium">{entry.task_title}</TableCell>
                          <TableCell>{entry.user_name}</TableCell>
                          <TableCell className="font-mono text-sm">
                            {new Date(entry.start_time).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {entry.end_time ? new Date(entry.end_time).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono font-semibold">
                            {formatDuration(entry.duration_minutes)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
